import './Stats.css'

import { useQuery } from '@tanstack/react-query'
import React, { useEffect } from 'react'
import { Button, Spinner } from 'react-bootstrap'
import { Area, AreaChart, Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'

import { getBrandLogo } from '../../../helpers/getBrandLogo'
import { QUERY_KEYS } from '../../../lib/queryKeys'
import { getPackagesStats } from '../../../services/packages'

export default function Stats() {
  const {
    data: statsData,
    isLoading,
    isError,
    error,
    refetch,
    isRefetching,
  } = useQuery({
    queryKey: [QUERY_KEYS.PACKAGES_STATS],
    queryFn: () => getPackagesStats({ isDraft: false }),
  })

  // Destructure data with fallbacks
  const packagesTotals = statsData?.totals || { totalPackages: 0, totalShipped: 0, totalUnited: 0 }
  const chartDataMonthP = statsData?.chartDataMonthP || []
  const chartDataMonthW = statsData?.chartDataMonthW || []
  const chartDataMonthUnited = statsData?.chartDataMonthUnited || []
  const chartDataDayP = statsData?.chartDataDayP || []
  const chartDataDayW = statsData?.chartDataDayW || []
  const brandsArr = statsData?.brandsArr || []
  const workers = statsData?.workers || []

  const formatMonthTooltipLabel = label => {
    if (label && label.includes('-')) {
      const date = new Date(label)
      return date.toLocaleDateString('en-US', {
        month: 'long',
        year: 'numeric',
      })
    }
  }

  const formatDayTooltipLabel = label => {
    // The label now contains the full date
    if (label && label.includes('-')) {
      const date = new Date(label)
      return date.toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      })
    }
    // Fallback for old format
    const dayNumber = parseInt(label)
    const currentYear = new Date().getFullYear()
    const currentMonth = new Date().getMonth() + 1
    const date = new Date(currentYear, currentMonth - 1, dayNumber)
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    })
  }
  const formatMonthAxis = label => {
    if (label && label.includes('-')) {
      return label.split('-')[1]
    }
    return label
  }
  const formatDayAxis = label => {
    if (label && label.includes('-')) {
      return label.split('-')[2]
    }
    return label
  }
  useEffect(() => {
    if (window.location.href.includes('office')) {
      document.getElementById('enable-toolbar').style.display = 'none'
    }
  }, [])

  if (isLoading) {
    return (
      <section className='stats-comp'>
        <h2 className='main-title'>Statistics</h2>
        <Spinner animation='border' variant='primary' />
      </section>
    )
  }

  if (isError) {
    return (
      <section className='stats-comp'>
        <h2 className='main-title'>Statistics</h2>
        <div className='stats-error'>
          <p>Error loading statistics: {error?.message || 'Unknown error'}</p>
          <Button onClick={() => refetch()} className='btn btn-primary' disabled={isRefetching}>
            {isRefetching ? 'Retrying...' : 'Retry'}
          </Button>
        </div>
      </section>
    )
  }

  return (
    <section className='stats-comp'>
      <div className='stats-header'>
        <h2>Statistics</h2>
      </div>

      <div className='stats-card'>Total packages: {packagesTotals.totalPackages}</div>
      <div className='stats-card'>Total Shipped packages: {packagesTotals.totalShipped}</div>
      <div className='stats-card'>Total united packages: {packagesTotals.totalUnited}</div>
      <Button size='sm' onClick={() => refetch()} disabled={isRefetching}>
        {isRefetching ? 'Refreshing...' : '🔄 Refresh Data'}
      </Button>
      {/* Worker Statistics Section */}
      {workers.length > 0 && (
        <div className='stats-section'>
          <h3>Worker Statistics</h3>
          <div className='worker-chart-container'>
            <ResponsiveContainer width='100%' height={450}>
              <BarChart
                data={workers.slice(0, 20).map(worker => ({
                  ...worker,
                  name: worker.email || worker.id || 'Unknown',
                  packages: worker.count,
                }))}
                margin={{ top: 20, right: 30, left: 20, bottom: 80 }}
              >
                <CartesianGrid strokeDasharray='3 3' />
                <XAxis dataKey='name' angle={-45} textAnchor='end' height={80} interval={0} tick={{ fontSize: 12 }} />
                <YAxis label={{ value: 'Number of Packages', angle: -90, position: 'insideLeft' }} tick={{ fontSize: 12 }} />
                <Tooltip formatter={value => [value, 'Packages']} labelFormatter={label => `Worker: ${label}`} />
                <Bar dataKey='packages' fill='#8884d8' radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <div className='chart-cont'>
        <h2>Packages, Monthly</h2>
        <ResponsiveContainer width='100%' height='45%'>
          <AreaChart data={chartDataMonthP} margin={{ top: 10, right: 10, left: 10, bottom: 50 }}>
            <defs>
              <linearGradient id='colorUv' x1='0' y1='0' x2='0' y2='1'>
                <stop offset='5%' stopColor='#8884d8' stopOpacity={0.8} />
                <stop offset='95%' stopColor='#8884d8' stopOpacity={0} />
              </linearGradient>
              <linearGradient id='colorPv' x1='0' y1='0' x2='0' y2='1'>
                <stop offset='5%' stopColor='#20c997' stopOpacity={0.8} />
                <stop offset='95%' stopColor='#20c997' stopOpacity={0} />
              </linearGradient>
            </defs>
            <XAxis dataKey='name' tickFormatter={formatMonthAxis} />
            <YAxis type='number' tickCount='12' />
            <CartesianGrid strokeDasharray='3 3' />
            <Tooltip labelFormatter={formatMonthTooltipLabel} />
            <Area type='monotone' dataKey='uv' name='Packages' stroke='#8884d8' fillOpacity={1} fill='url(#colorUv)' />
            <Area type='monotone' dataKey='pv' name='Shipped Packages' stroke='#20c997' fillOpacity={1} fill='url(#colorPv)' />
          </AreaChart>
        </ResponsiveContainer>

        <h2>Packages by weight, Monthly</h2>
        <ResponsiveContainer width='100%' height='45%'>
          <AreaChart data={chartDataMonthW} margin={{ top: 10, right: 10, left: 10, bottom: 50 }}>
            <defs>
              <linearGradient id='colorUv' x1='0' y1='0' x2='0' y2='1'>
                <stop offset='5%' stopColor='#8884d8' stopOpacity={0.8} />
                <stop offset='95%' stopColor='#8884d8' stopOpacity={0} />
              </linearGradient>
              <linearGradient id='colorPv' x1='0' y1='0' x2='0' y2='1'>
                <stop offset='5%' stopColor='#20c997' stopOpacity={0.8} />
                <stop offset='95%' stopColor='#20c997' stopOpacity={0} />
              </linearGradient>
            </defs>
            <XAxis dataKey='name' tickFormatter={formatMonthAxis} />
            <YAxis type='number' tickCount='12' />
            <CartesianGrid strokeDasharray='3 3' />
            <Tooltip labelFormatter={formatMonthTooltipLabel} />
            <Area type='monotone' dataKey='uv' name='Packages (KG)' stroke='#8884d8' fillOpacity={1} fill='url(#colorUv)' />
            <Area type='monotone' dataKey='pv' name='Shipped Packages (KG)' stroke='#20c997' fillOpacity={1} fill='url(#colorPv)' />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <div className='stats-logos-conts'>
        {brandsArr.length > 0 &&
          brandsArr.slice(0, 18).map((obj, idx) => {
            return (
              <div className='card' key={idx}>
                <img src={getBrandLogo(obj.brand)} />
                <span>{obj.brand}</span>
                <span> {Math.round(obj.count * 100)}% </span>
              </div>
            )
          })}
      </div>

      <div className='stats-small-charts'>
        <div className='ssc-cont'>
          <h4>Packages, Daily</h4>
          <ResponsiveContainer width='100%' height='100%'>
            <AreaChart data={chartDataDayP} margin={{ top: 10, right: 10, left: 10, bottom: 50 }}>
              <defs>
                <linearGradient id='colorUv' x1='0' y1='0' x2='0' y2='1'>
                  <stop offset='5%' stopColor='#8884d8' stopOpacity={0.8} />
                  <stop offset='95%' stopColor='#8884d8' stopOpacity={0} />
                </linearGradient>
                <linearGradient id='colorPv' x1='0' y1='0' x2='0' y2='1'>
                  <stop offset='5%' stopColor='#20c997' stopOpacity={0.8} />
                  <stop offset='95%' stopColor='#20c997' stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey='name' tickFormatter={formatDayAxis} />
              <YAxis type='number' tickCount='12' />
              <CartesianGrid strokeDasharray='3 3' />
              <Tooltip labelFormatter={formatDayTooltipLabel} />
              <Area type='monotone' dataKey='uv' name='Packages' stroke='#8884d8' fillOpacity={1} fill='url(#colorUv)' />
              <Area type='monotone' dataKey='pv' name='Shipped Packages' stroke='#20c997' fillOpacity={1} fill='url(#colorPv)' />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className='ssc-cont'>
          <h4>Packages by weight, Daily</h4>
          <ResponsiveContainer width='100%' height='100%'>
            <AreaChart data={chartDataDayW} margin={{ top: 10, right: 10, left: 10, bottom: 50 }}>
              <defs>
                <linearGradient id='colorUv' x1='0' y1='0' x2='0' y2='1'>
                  <stop offset='5%' stopColor='#8884d8' stopOpacity={0.8} />
                  <stop offset='95%' stopColor='#8884d8' stopOpacity={0} />
                </linearGradient>
                <linearGradient id='colorPv' x1='0' y1='0' x2='0' y2='1'>
                  <stop offset='5%' stopColor='#20c997' stopOpacity={0.8} />
                  <stop offset='95%' stopColor='#20c997' stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey='name' tickFormatter={formatDayAxis} />
              <YAxis type='number' tickCount='12' />
              <CartesianGrid strokeDasharray='3 3' />
              <Tooltip labelFormatter={formatDayTooltipLabel} />
              <Area type='monotone' dataKey='uv' name='Packages (KG)' stroke='#8884d8' fillOpacity={1} fill='url(#colorUv)' />
              <Area type='monotone' dataKey='pv' name='Shipped Packages (KG)' stroke='#20c997' fillOpacity={1} fill='url(#colorPv)' />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className='stats-small-charts'>
        <div className='ssc-cont'>
          <h4>Packages difference, Daily</h4>
          <ResponsiveContainer width='100%' height='100%'>
            <AreaChart data={chartDataDayP} margin={{ top: 10, right: 10, left: 10, bottom: 50 }}>
              <defs>
                <linearGradient id='colorDv' x1='0' y1='0' x2='0' y2='1'>
                  <stop offset='5%' stopColor='#dc3545' stopOpacity={0.8} />
                  <stop offset='95%' stopColor='#dc3545' stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey='name' tickFormatter={formatDayAxis} />
              <YAxis type='number' tickCount='12' />
              <CartesianGrid strokeDasharray='3 3' />
              <Tooltip labelFormatter={formatDayTooltipLabel} />
              <Area type='monotone' dataKey='difference' name='Difference' stroke='#dc3545' fillOpacity={1} fill='url(#colorDv)' />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className='ssc-cont'>
          <h4>Packages weight difference, Daily</h4>
          <ResponsiveContainer width='100%' height='100%'>
            <AreaChart data={chartDataDayW} margin={{ top: 10, right: 10, left: 10, bottom: 50 }}>
              <defs>
                <linearGradient id='colorDv' x1='0' y1='0' x2='0' y2='1'>
                  <stop offset='5%' stopColor='#dc3545' stopOpacity={0.8} />
                  <stop offset='95%' stopColor='#dc3545' stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey='name' tickFormatter={formatDayAxis} />
              <YAxis type='number' tickCount='12' />
              <CartesianGrid strokeDasharray='3 3' />
              <Tooltip labelFormatter={formatDayTooltipLabel} />
              <Area type='monotone' dataKey='difference' name='Difference (KG)' stroke='#dc3545' fillOpacity={1} fill='url(#colorDv)' />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className='stats-small-charts'>
        <div className='ssc-cont'>
          <h4>United Packages, Monthly</h4>
          <ResponsiveContainer width='100%' height='100%'>
            <AreaChart data={chartDataMonthUnited} margin={{ top: 10, right: 10, left: 10, bottom: 50 }}>
              <defs>
                <linearGradient id='colorUv' x1='0' y1='0' x2='0' y2='1'>
                  <stop offset='5%' stopColor='#8884d8' stopOpacity={0.8} />
                  <stop offset='95%' stopColor='#8884d8' stopOpacity={0} />
                </linearGradient>
                <linearGradient id='colorPv' x1='0' y1='0' x2='0' y2='1'>
                  <stop offset='5%' stopColor='#20c997' stopOpacity={0.8} />
                  <stop offset='95%' stopColor='#20c997' stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey='name' tickFormatter={formatMonthAxis} />
              <YAxis type='number' tickCount='12' />
              <CartesianGrid strokeDasharray='3 3' />
              <Tooltip labelFormatter={formatMonthTooltipLabel} />
              <Area type='monotone' dataKey='pv' name='Before United Packages' stroke='#20c997' fillOpacity={1} fill='url(#colorPv)' />
              <Area type='monotone' dataKey='uv' name='United Packages' stroke='#8884d8' fillOpacity={1} fill='url(#colorUv)' />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
    </section>
  )
}
